import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { ListQuery } from "../../../Types/CommonTypes";
import { PaymentMethodViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";

import LoadingSection from "../../../components/LoadingSection";
import ResponsivePagination from "../../../components/ResponsivePagination";
const PaymentMethodList: React.FC = () => {
    const { t } = useTranslation();
    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();

    const [items, setItems] = useState<PaymentMethodViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "" });
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(false);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{
                items: PaymentMethodViewModel[];
                count: number;
            }>("/settingsApi/GetPaymentMethodList", query);
            setItems(response.items);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [query]);

    useEffect(() => {
        loading ? showLoading() : hideLoading();
    }, [loading]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (id?: number | null) => {
        navigate({ path: "/settings/paymentmethod/edit", params: { id } });
    };



    const handleCreate = () => {
        navigate({ path: "/settings/paymentmethod/edit", params: {} });
    };

    return (
        <CenteredListCard title={t("common.paymentMethod")}> 
         
                <Box sx={{ marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleCreate}
                    >
                        {t("common.create")}
                    </Button>
                </Box>
                <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                        label={t("common.search")}
                        variant="outlined"
                        fullWidth
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        disabled={loading}
                    />
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                        {t("common.search")}
                    </Button>
                </Box>

            <LoadingSection loading={loading}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {items.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <Card
                                onClick={() => handleSelect(item.id)}
                                sx={{
                                    cursor: "pointer",
                                    transition: "box-shadow 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow: 4,
                                        backgroundColor: "secondary.main",
                                    },
                                }}
                            >
                                <CardContent>
                                    <ListCardHeader
                                        icon={<CreditCardIcon fontSize="small" color="action" />}
                                        title={item.id}
                                    />
                                    <Typography variant="body2" color="textSecondary">
                                        {t("common.description")}: {item.description}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t("common.code")}: {item.paymentCode}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {(() => {
                                            const key = `common.paymentType_${item.paymentTypeDesc?.replace(/\s+/g, "_")}`;
                                            const translated = t(key, {
                                                defaultValue: item.paymentTypeDesc ?? "-"
                                            });
                                            return `${t("common.type")}: ${translated}`;
                                        })()}
                                       
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {items.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                        </Grid>
                    )}
                </Grid>
            </LoadingSection>

                <ResponsivePagination


                    page={query.page}


                    pageSize={query.pageSize}


                    totalCount={totalCount}


                    onPageChange={handlePageChange}


                />
            
        </CenteredListCard>
    );
};

export default PaymentMethodList;